<template>
  <div class="container">
    <TrfHeader pageTitle="Tarefas do cliente" />

    <!-- Modais -->
    <TrfModalConcluirTarefa
      v-if="isModalConcluirAtivo"
      :isAtivo="isModalConcluirAtivo"
      :tarefa="tarefaSelecionada"
      @confirmar="confirmarConclusao"
      @fechar="fecharModalConcluir"
    />

    <TrfModalTransferirTarefa
      v-if="isModalTransferirAtivo"
      :isAtivo="isModalTransferirAtivo"
      :tarefa="tarefaSelecionada"
      :listaDepartamentos="listaDepartamentos"
      @confirmar="confirmarTransferencia"
      @fechar="fecharModalTransferir"
    />

    <TrfModalExcluirTarefa
      v-if="isModalExcluirAtivo"
      :isAtivo="isModalExcluirAtivo"
      :tarefa="tarefaSelecionada"
      :textoExclusao="textoExclusao"
      @confirmar="confirmarExclusao"
      @fechar="fecharModalExcluir"
    />

    <TrfSimpleModal
      :titulo="tituloModalDetalhesHistorico"
      :isAtivo="isModalDetalhesHistoricoAtivo"
      @close="fecharModalDetalhesHistorico"
      :isModalPersonalizado="true"
    >
      <TrfDetalhesHistorico
        v-if="isModalDetalhesHistoricoAtivo"
        :tarefa="detalhesHistorico"
      />
    </TrfSimpleModal>

    <!-- Filtros -->
    <div class="filter-container" v-e2e="'filtro-tarefas-cliente'">
      <TrfFilter
        :configuration="filterConfig"
        @update:query-string="setQuery"
      />
    </div>

    <!-- Tabela -->
    <div class="pt-2">
      <TrfGlobalLoader v-if="statusTabela === 'is-loading'" type="overlay" />
      <div v-e2e="'tabela-tarefas-cliente'">
        <TrfSimpleTable
          :configuracaoDeColunas="colunas"
          :dados="tarefas"
          @duploClick="iniciarModalDetalhesHistorico"
          mensagemSemDados="Nenhuma tarefa foi encontrada."
        >
          <template #th>
            <th class="has-text-centered">Status</th>
            <th></th>
          </template>
          <template #td="{ item }">
            <td v-if="item.statusId" class="has-text-centered">
              <TrfTag 
                :texto="ajustarTextoStatus(statusTarefaEnum.getById(item.statusId).getTexto())"
                :cor="statusTarefaEnum.getById(item.statusId).getCor()"
              />
            </td>
            <td>
              <div
                class="dropdown is-right"
                :class="{'is-active': dropdownAtivo === item.id}"
                @click.stop="toggleDropdown(item.id, $event)"
                v-if="item.statusId !== statusTarefaEnum.RECEBIDOS.getId()"
              >
                <div class="dropdown-trigger">
                  <span class="icon is-small">
                    <i class="mdi mdi-dots-horizontal mdi-24px" aria-hidden="true"></i>
                  </span>
                </div>
                <div class="dropdown-menu" role="menu">
                  <div class="dropdown-content is-pulled-right">
                    <a
                      class="dropdown-item"
                      @click="abrirModalConcluir(item)"
                      v-e2e="'btn-concluir-tarefa-cliente'"
                    >
                      <i class="mdi mdi-check-bold mdi-14px" aria-hidden="true"></i> Concluir
                    </a>
                    <a
                      class="dropdown-item"
                      @click="abrirModalTransferir(item)"
                      v-e2e="'btn-transferir-tarefa-cliente'"
                    >
                      <i class="mdi mdi-share mdi-14px" aria-hidden="true"></i> Transferir
                    </a>
                    <a
                      class="dropdown-item"
                      @click="abrirModalExcluir(item)"
                      v-e2e="'btn-excluir-tarefa-cliente'"
                    >
                      <i class="mdi mdi-delete mdi-14px" aria-hidden="true"></i> Excluir
                    </a>
                  </div>
                </div>
              </div>
            </td>
          </template>
        </TrfSimpleTable>
      </div>

      <TrfPagination
        v-if="paginacao.total > 0"
        class="mt-5"
        :total="paginacao.total"
        :quantidadeDeItemsExibindo="tarefas.length"
        :paginaSelecionada="paginacao.pagina"
        :limiteAtual="paginacao.limite"
        @changePage="atualizarPagina"
        @changeLimite="atualizarLimite"
      />
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { service } from '../Dashboard/service/historico-tarefas-service';
import { usuarioService } from '../../service/usuario-service';
import { empresasService } from '../../service/empresas-service';
import { departamentoService } from '../../service/departamento-service';
import { utils } from '../../util/utils';
import { toastUtils } from '../../util/toast.utils';
import { statusTarefaEnum } from '../../enums/statusTarefaEnum';
import { tipoUsuarioEnum } from '../../enums/tipoUsuarioEnum';
import { ConfigFiltro, ContainerOptions } from '../../components/Filter/models/configFiltro.model';
import { ConfigCampoFiltro, configDeCampos } from '../../components/Filter/models/configCampoFiltro.model';
import { CheckboxFieldOption } from '../../components/Filter/models/InputCheckboxFieldsConfig.model.js';
import { 
  defaultButtonFiltrar, 
  defaultButtonSelectorIntervalField, 
  defaultCheckboxFieldsStatusFilter, 
  defaultIlikeTextField, 
  defaultPeriodoDataConclusaoField, 
  defaultSelectResponsavelField
} from '../../components/Filter/config/filterDefaultConfig';
import { clearFilter, clearInvalidIntervals } from '../../components/Filter/utils/filter.utils';

export default {
  name: 'TarefasCliente',
  components: {
    TrfHeader: defineAsyncComponent(() => import('../../components/Header')),
    TrfSimpleTable: defineAsyncComponent(() => import('../../components/SimpleTable')),
    TrfFilter: defineAsyncComponent(() => import('../../components/Filter')),
    TrfTag: defineAsyncComponent(() => import('../../components/Tag')),
    TrfPagination: defineAsyncComponent(() => import('../../components/Paginacao')),
    TrfModalConcluirTarefa: defineAsyncComponent(() => import('../../components/Modal/TrfModalConcluirTarefa')),
    TrfModalTransferirTarefa: defineAsyncComponent(() => import('../../components/Modal/TrfModalTransferirTarefa')),
    TrfModalExcluirTarefa: defineAsyncComponent(() => import('../../components/Modal/TrfModalExcluirTarefa')),
    TrfSimpleModal: defineAsyncComponent(() => import('../../components/Modal/TrfSimpleModal.vue')),
    TrfDetalhesHistorico: defineAsyncComponent(() => import('../../components/DetalhesHistorico')),
    TrfGlobalLoader: defineAsyncComponent(() => import('../../components/GlobalLoader'))
  },
  data() {
    return {
      tarefas: [],
      statusTabela: 'is-loading',
      paginacao: {
        pagina: 1,
        limite: 10,
        total: 0
      },
      responsaveisCliente: [],
      listaEmpresas: [],
      listaDepartamentos: [],
      filterConfig: new ConfigFiltro(),
      filtro: {
        status: [statusTarefaEnum.AGUARDANDO_CLIENTE.getId().toString(), statusTarefaEnum.ATRASADAS.getId().toString()]
      },
      invalidFilterConclusao: false,
      dropdownAtivo: null,
      isModalConcluirAtivo: false,
      isModalTransferirAtivo: false,
      isModalExcluirAtivo: false,
      isModalDetalhesHistoricoAtivo: false,
      tarefaSelecionada: null,
      textoExclusao: '',
      tituloModalDetalhesHistorico: '',
      detalhesHistorico: null,
      colunas: [
        { titulo: 'Nome da tarefa', referencia: ['nome'] },
        { titulo: 'Competência', referencia: ['dataCompetencia'] },
        { titulo: 'Empresa', referencia: ['empresaNomeFormatado'] },
        { titulo: 'Data de entrega', referencia: ['dataEntrega'] },
        { titulo: 'Data de conclusão', referencia: ['dataConclusao'] }
      ],
      statusTarefaEnum,
      filtroInicializado: false
    };
  },
  computed: {
    filtrosRequisicao() {
      return {
        'sort[ASC]': 'prazoEntrega,id',
        pagina: this.paginacao.pagina,
        limit: this.paginacao.limite,
        tarefaCliente: true,
        ...this.filtro
      };
    }
  },
  methods: {
    async carregarTarefas(reiniciarPaginacao = false) {
      try {
        if (reiniciarPaginacao) {
          this.paginacao.pagina = 1;
        }
        
        this.statusTabela = 'is-loading';
        
        const response = await service.listarHistorico(this.filtrosRequisicao);
        
        this.paginacao.total = parseInt(response.totalDeItens);
        this.tarefas = this.paginacao.total > 0 ? this.converterParaDto(response.data) : [];
        this.statusTabela = this.paginacao.total === 0 ? 'is-empty' : '';
      } catch (error) {
        console.error('Erro ao carregar tarefas:', error);
        this.statusTabela = 'is-danger';
        toastUtils.dangerToast('Erro ao carregar tarefas!');
      }
    },
    
    converterParaDto(tarefas) {
      return tarefas.map(tarefa => ({
        ...tarefa,
        dataCompetencia: tarefa.textoPeriodoReferencia || '---',
        empresaNomeFormatado: `${tarefa.empresa.externoId} - ${tarefa.empresa.nome}`,
        dataEntrega: utils.formatarDataParaExibicaoNoDataTable(tarefa.prazoEntrega),
        dataConclusao: tarefa.dataConclusao 
          ? utils.formatarDataParaExibicaoNoDataTable(tarefa.dataConclusao)
          : '---',
        status: tarefa.status.nome,
        statusId: tarefa.status.id,
        responsavel: tarefa.responsavel?.nome || '---',
        departamento: tarefa.departamento?.nome || tarefa.departamentoPadrao?.nome || '---',
        data: utils.formatarDataParaExibicaoNoDataTable(tarefa.prazoEntrega),
        prazoLegal: utils.formatarDataParaExibicaoNoDataTable(tarefa.prazoLegal)
      }));
    },
    
    async carregarResponsaveisCliente() {
      try {
        this.responsaveisCliente = await usuarioService.listarUsuariosPorTipo(tipoUsuarioEnum.CLIENTE.getId());
      } catch (error) {
        console.error('Erro ao carregar responsáveis:', error);
      }
    },
    
    async carregarEmpresas() {
      try {
        this.listaEmpresas = await empresasService.listarEmpresas();
      } catch (error) {
        console.error('Erro ao carregar empresas:', error);
      }
    },
    
    async carregarDepartamentos() {
      try {
        this.listaDepartamentos = await departamentoService.listar();
      } catch (error) {
        console.error('Erro ao carregar departamentos:', error);
      }
    },
    
    inicializarFiltro() {
      if (this.filtroInicializado) return;
      
      const responsaveisOptions = [
        { value: 'sem responsavel', label: 'Sem Responsável' },
        ...this.responsaveisCliente
      ];
      
      const selectResponsavel = defaultSelectResponsavelField('responsavelId', 'select-responsavel-filtro', 'Responsável', responsaveisOptions);
      selectResponsavel.containerOptions.containerClasses = {
        ...selectResponsavel.containerOptions.containerClasses,
        'is-4': false,
        'is-3': true
      };
      
      const selectEmpresa = defaultSelectResponsavelField('empresaId', 'select-empresa-filtro', 'Empresa', this.listaEmpresas);
      selectEmpresa.containerOptions.containerClasses = {
        ...selectEmpresa.containerOptions.containerClasses,
        'is-4': false,
        'is-3': true
      };
      
      const buttonSelectorFiltro = defaultButtonSelectorIntervalField('prazoEntrega', 'intervaloFiltro');
      buttonSelectorFiltro.configuracoesDeInput.options[0].isSelected = false;
      
      const campoBotao = defaultButtonFiltrar('buttonFiltrar', 'buttonFiltrar', () => this.carregarTarefas(true));
      
      const checkboxFields = defaultCheckboxFieldsStatusFilter('status', 'checkbox-fields-status-filtro');
      checkboxFields.configuracoesDeInput.options = [
        new CheckboxFieldOption('checkbox-status-aguardando-cliente', 'Aguardando cliente', true, statusTarefaEnum.AGUARDANDO_CLIENTE.getId().toString()),
        new CheckboxFieldOption('checkbox-status-atrasada', 'Atrasada', true, statusTarefaEnum.ATRASADAS.getId().toString()),
        new CheckboxFieldOption('checkbox-status-recebido', 'Recebido', false, statusTarefaEnum.RECEBIDOS.getId().toString())
      ];
      
      const textFiltro = defaultIlikeTextField('nome', 'text-nome-tarefa-filtro', 'Nome da tarefa', 'Pesquise pelo nome da tarefa');
      textFiltro.containerOptions.containerClasses = {
        ...textFiltro.containerOptions.containerClasses,
        'is-4': false,
        'is-3': true
      };
      
      const selectDepartamento = defaultSelectResponsavelField('departamentoId', 'select-departamento-filtro', 'Departamento', this.listaDepartamentos);
      selectDepartamento.containerOptions.containerClasses = {
        ...selectDepartamento.containerOptions.containerClasses,
        'is-4': false,
        'is-3': true
      };
      
      const periodoEntregaFiltro = defaultPeriodoDataConclusaoField('prazoEntrega', 'periodoEntregaFiltro', 'Data de entrega');
      periodoEntregaFiltro.containerOptions.containerClasses = {
        ...periodoEntregaFiltro.containerOptions.containerClasses,
        'is-5': false,
        'is-3': true
      };
      periodoEntregaFiltro.configuracoesDeInput.nameCampoInicial = 'De';
      periodoEntregaFiltro.configuracoesDeInput.nameCampoFinal = 'Até';
      
      const periodoFiltro = defaultPeriodoDataConclusaoField('dataConclusao', 'periodoConclusaoFiltro', 'Data de conclusão');
      periodoFiltro.containerOptions.containerClasses = {
        ...periodoFiltro.containerOptions.containerClasses,
        'is-5': false,
        'is-3': true
      };
      periodoFiltro.configuracoesDeInput.nameCampoInicial = 'De';
      periodoFiltro.configuracoesDeInput.nameCampoFinal = 'Até';
      
      const espacadorStatus = new ConfigCampoFiltro(
        new configDeCampos('espacador-status', 'spacer', '', '')
      );
      espacadorStatus.containerOptions = new ContainerOptions({'column': true, 'is-3': true}, {});
      espacadorStatus.configuracoesDeInput = {};
      
      this.filterConfig.options = [
        selectResponsavel,
        selectEmpresa,
        buttonSelectorFiltro,
        campoBotao,
        espacadorStatus,
        checkboxFields
      ];
      
      this.filterConfig.advancedOptions = [
        textFiltro,
        selectDepartamento,
        periodoEntregaFiltro,
        periodoFiltro
      ];
      
      this.filtroInicializado = true;
    },
    

    ajustarTextoStatus(statusTexto) {
      const ajustes = {
        'Atrasadas': 'Atrasada',
        'Recebidos': 'Recebido'
      };
      return ajustes[statusTexto] || statusTexto;
    },
    
    setQuery(valor) {
      if (!this.filtroInicializado) return;
      
      let valorLocal = {...valor};
      
      if (!this.isFiltroConclusaoValido(valorLocal)) {
        this.invalidFilterConclusao = true;
      } else {
        this.invalidFilterConclusao = false;
      }
      
      const isSemResponsavel = valorLocal.responsavelId === 'sem responsavel';
      
      valorLocal = clearInvalidIntervals(valorLocal);
      valorLocal = clearFilter(valorLocal);
      
      if (isSemResponsavel) {
        delete valorLocal.responsavelId;
        valorLocal['responsavelId[is]'] = null;
      }
      
      valorLocal.tarefaCliente = true;
      
      this.filtro = valorLocal;
    },
    
    isFiltroConclusaoValido(valor) {
      if (!valor.dataConclusao) return true;
      
      const dataInicialNaoExiste = !valor.dataConclusao['dataConclusao[ge]'] && !!valor.dataConclusao['dataConclusao[le]'];
      const dataFinalNaoExiste = !!valor.dataConclusao['dataConclusao[ge]'] && !valor.dataConclusao['dataConclusao[le]'];
      const dataFinalIsMenorQueInicial = new Date(valor.dataConclusao['dataConclusao[le]']) < new Date(valor.dataConclusao['dataConclusao[ge]']);
      
      return !dataInicialNaoExiste && !dataFinalNaoExiste && !dataFinalIsMenorQueInicial;
    },
    
    aplicarFiltros() {
      this.carregarTarefas(true);
    },
    
    atualizarPagina(pagina) {
      this.paginacao.pagina = pagina;
      this.carregarTarefas();
    },
    
    atualizarLimite(limite) {
      this.paginacao.limite = limite;
      this.carregarTarefas();
    },
    
    toggleDropdown(id, event) {
      event.preventDefault();
      event.stopPropagation();
      this.dropdownAtivo = this.dropdownAtivo === id ? null : id;
    },
    
    fecharDropdowns() {
      this.dropdownAtivo = null;
    },
    
    abrirModalConcluir(tarefa) {
      this.tarefaSelecionada = tarefa;
      this.isModalConcluirAtivo = true;
      this.dropdownAtivo = null;
    },
    
    async confirmarConclusao(tarefa) {
      try {
        const sucesso = await service.concluirManualmenteDashboardDoCliente(tarefa.id);
        
        if (sucesso) {
          this.fecharModalConcluir();
          await this.carregarTarefas();
        }
      } catch (error) {
        console.error('Erro ao concluir tarefa:', error);
      }
    },
    
    fecharModalConcluir() {
      this.isModalConcluirAtivo = false;
      this.tarefaSelecionada = null;
    },
    
    abrirModalTransferir(tarefa) {
      this.tarefaSelecionada = tarefa;
      this.isModalTransferirAtivo = true;
      this.dropdownAtivo = null;
    },
    
    async confirmarTransferencia({ tarefa, novoDepartamentoId }) {
      try {
        const response = await service.transferirResponsavelEDepartamento(
          [tarefa.id],
          '',
          novoDepartamentoId,
          [],
          true
        );
        
        if (response.ok) {
          this.fecharModalTransferir();
          await this.carregarTarefas();
        }
      } catch (error) {
        console.error('Erro ao transferir tarefa:', error);
      }
    },
    
    fecharModalTransferir() {
      this.isModalTransferirAtivo = false;
      this.tarefaSelecionada = null;
    },
    
    abrirModalExcluir(tarefa) {
      this.tarefaSelecionada = tarefa;
      this.textoExclusao = `Deseja excluir a tarefa <strong>${tarefa.nome}</strong>?`;
      this.isModalExcluirAtivo = true;
      this.dropdownAtivo = null;
    },
    
    async confirmarExclusao(tarefa) {
      try {
        const response = await service.delete(tarefa);
        
        if (response.ok) {
          toastUtils.successToast('Tarefa excluída com sucesso!');
          this.fecharModalExcluir();
          await this.carregarTarefas();
        }
      } catch (error) {
        console.error('Erro ao excluir tarefa:', error);
        toastUtils.dangerToast('Erro ao excluir tarefa!');
      }
    },
    
    fecharModalExcluir() {
      this.isModalExcluirAtivo = false;
      this.tarefaSelecionada = null;
      this.textoExclusao = '';
    },
    
    iniciarModalDetalhesHistorico(tarefa) {
      this.isModalDetalhesHistoricoAtivo = true;
      this.tituloModalDetalhesHistorico = this.gerarTituloModalDetalhesHistorico(tarefa);
      this.detalhesHistorico = tarefa;
    },
    
    fecharModalDetalhesHistorico() {
      this.isModalDetalhesHistoricoAtivo = false;
      this.tituloModalDetalhesHistorico = '';
      this.detalhesHistorico = null;
    },
    
    gerarTituloModalDetalhesHistorico(tarefa) {
      const nome = tarefa.nome || '';
      const competencia = tarefa.textoPeriodoReferencia || '';
      return `${nome} - ${competencia}`;
    },
    async inicializarPagina() {
      try {
        await Promise.all([
          this.carregarResponsaveisCliente(),
          this.carregarEmpresas(),
          this.carregarDepartamentos()
        ]);
        
        this.inicializarFiltro();
        await this.$nextTick();
        await this.carregarTarefas();
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
        toastUtils.dangerToast('Erro ao carregar dados da página!');
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.fecharDropdowns);
    this.inicializarPagina();
  },
  
  beforeUnmount() {
    document.removeEventListener('click', this.fecharDropdowns);
  }
};
</script>

<style lang="scss" scoped>
.filter-container :deep(.columns > .column #checkbox-fields-status-filtro-input-checkbox-fields-status) {
  margin-top: -1.5em
}

:deep(.is-advanced-filter-box .columns.is-multiline > .column.is-12.mb-0) {
  margin-bottom: 5px !important;
}

:deep(.is-advanced-filter-box .columns.is-multiline > .column.is-6) {
  padding-top: 0;
  padding-bottom: 0;
}

.dropdown {
  cursor: pointer;
}

.dropdown-menu {
  min-width: 10rem;
}

.dropdown-item {
  padding-right: 1rem;
}
</style>